import React, { useState, useEffect, useRef, useCallback } from "react";
import { Live, Spot } from "@/common/model/Live";
import { SpotRequest, MergeSpotRequest, SpotAddRequest } from "@/common/model/SpotRequest";
import { LiveService } from "@/services/LiveService";
import { ENDPOINTS } from "@/constants/api";
import { useAuth } from "@/context/AuthContext";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

// Nominatim location result
interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  type: string;
  importance: number;
}

interface MapSearchBarProps {
  sessions: Live[];
  onSpotSelect: (spot: Spot) => void;
  onLocationSelect: (lat: number, lng: number, name: string) => void;
  onUserSelect: (sessionId: number) => void;
  // Request mode props
  spotRequests?: SpotRequest[];
  onRequestSelect?: (request: SpotRequest) => void;
}

const INITIAL_LIMIT = 5;

const DEFAULT_PROFILE_PIC =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236b7280'%3E%3Ccircle cx='12' cy='12' r='12' fill='%23e5e7eb'/%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%239ca3af'/%3E%3C/svg%3E";

const MapSearchBar: React.FC<MapSearchBarProps> = ({
  sessions,
  onSpotSelect,
  onLocationSelect,
  onUserSelect,
  spotRequests = [],
  onRequestSelect,
}) => {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [spots, setSpots] = useState<Spot[]>([]);
  const [allSpots, setAllSpots] = useState<Spot[]>([]); // Full results for "show more"
  const [locations, setLocations] = useState<NominatimResult[]>([]);
  const [isSearchingSpots, setIsSearchingSpots] = useState(false);
  const [isSearchingLocations, setIsSearchingLocations] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const spotDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const locationDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Expanded states for each section
  const [expandedSections, setExpandedSections] = useState<{
    requests: boolean;
    riders: boolean;
    spots: boolean;
    locations: boolean;
  }>({ requests: false, riders: false, spots: false, locations: false });

  // Reset expanded states when query changes
  useEffect(() => {
    setExpandedSections({ requests: false, riders: false, spots: false, locations: false });
  }, [query]);

  // Filter spot requests by name (client-side)
  const filteredRequests = query.trim().length >= 2
    ? spotRequests.filter((r) => {
        const q = query.toLowerCase();
        if (r.type === "merge") {
          const data = r.data as MergeSpotRequest;
          return (
            data.sourcespotname?.toLowerCase().includes(q) ||
            data.targetspotname?.toLowerCase().includes(q)
          );
        } else {
          const data = r.data as SpotAddRequest;
          return (
            data.spotname?.toLowerCase().includes(q) ||
            data.oldname?.toLowerCase().includes(q) ||
            data.newname?.toLowerCase().includes(q)
          );
        }
      })
    : [];

  // Filter live users by name (client-side)
  const filteredUsers = query.trim().length >= 2
    ? sessions.filter(
        (s) =>
          s.user.name.toLowerCase().includes(query.toLowerCase()) ||
          s.user.username?.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  // Search spots (API with debounce)
  useEffect(() => {
    if (spotDebounceRef.current) {
      clearTimeout(spotDebounceRef.current);
    }

    if (!user?.accesstoken || !query.trim() || query.length < 2) {
      setSpots([]);
      setAllSpots([]);
      setIsSearchingSpots(false);
      return;
    }

    setIsSearchingSpots(true);
    spotDebounceRef.current = setTimeout(async () => {
      try {
        const results = await LiveService.searchSpots(query, user.accesstoken);
        setAllSpots(results); // Store all results
        setSpots(results.slice(0, INITIAL_LIMIT));
      } catch (error) {
        console.error("Error searching spots:", error);
        setSpots([]);
        setAllSpots([]);
      } finally {
        setIsSearchingSpots(false);
      }
    }, 300);

    return () => {
      if (spotDebounceRef.current) {
        clearTimeout(spotDebounceRef.current);
      }
    };
  }, [query, user?.accesstoken]);

  // Search locations via Nominatim (free OpenStreetMap geocoding)
  useEffect(() => {
    if (locationDebounceRef.current) {
      clearTimeout(locationDebounceRef.current);
    }

    if (!query.trim() || query.length < 3) {
      setLocations([]);
      setIsSearchingLocations(false);
      return;
    }

    setIsSearchingLocations(true);
    locationDebounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=0`,
          {
            headers: {
              "Accept-Language": "en",
            },
          }
        );
        const data: NominatimResult[] = await response.json();
        setLocations(data);
      } catch (error) {
        console.error("Error searching locations:", error);
        setLocations([]);
      } finally {
        setIsSearchingLocations(false);
      }
    }, 500); // Longer debounce for external API

    return () => {
      if (locationDebounceRef.current) {
        clearTimeout(locationDebounceRef.current);
      }
    };
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSpotClick = useCallback(
    (spot: Spot) => {
      onSpotSelect(spot);
      setQuery("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onSpotSelect]
  );

  const handleLocationClick = useCallback(
    (location: NominatimResult) => {
      onLocationSelect(
        parseFloat(location.lat),
        parseFloat(location.lon),
        location.display_name.split(",")[0]
      );
      setQuery("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onLocationSelect]
  );

  const handleUserClick = useCallback(
    (sessionId: number) => {
      onUserSelect(sessionId);
      setQuery("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onUserSelect]
  );

  const handleRequestClick = useCallback(
    (request: SpotRequest) => {
      onRequestSelect?.(request);
      setQuery("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onRequestSelect]
  );

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const isSearching = isSearchingSpots || isSearchingLocations;
  const hasResults = spots.length > 0 || locations.length > 0 || filteredUsers.length > 0 || filteredRequests.length > 0;
  const showDropdown = isOpen && query.trim().length >= 2;

  // Calculate display items for each section
  const displayedRequests = expandedSections.requests ? filteredRequests : filteredRequests.slice(0, INITIAL_LIMIT);
  const displayedUsers = expandedSections.riders ? filteredUsers : filteredUsers.slice(0, INITIAL_LIMIT);
  const displayedSpots = expandedSections.spots ? allSpots : spots;
  const displayedLocations = expandedSections.locations ? locations : locations.slice(0, INITIAL_LIMIT);

  // Shorten location display name
  const shortenDisplayName = (name: string) => {
    const parts = name.split(",").map((p) => p.trim());
    if (parts.length <= 3) return name;
    return `${parts[0]}, ${parts[1]}, ${parts[parts.length - 1]}`;
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      {/* Search Input */}
      <div className="flex items-center bg-gray-100 border border-gray-200 rounded focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
        {isSearching ? (
          <svg
            className="animate-spin w-4 h-4 text-gray-400 ml-2 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search spots, locations, or riders..."
          className="flex-1 px-2 py-1.5 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none text-sm"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setSpots([]);
              setLocations([]);
              inputRef.current?.focus();
            }}
            className="pr-2"
          >
            <svg
              className="w-4 h-4 text-gray-400 hover:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <div className="absolute z-[100] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-y-auto">
          {!hasResults && !isSearching && (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">
              No results found for "{query}"
            </div>
          )}

          {/* Spot Requests Section */}
          {filteredRequests.length > 0 && onRequestSelect && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Requests ({filteredRequests.length})
              </div>
              {displayedRequests.map((request) => {
                const isMerge = request.type === "merge";
                const data = request.data as MergeSpotRequest | SpotAddRequest;
                const name = isMerge
                  ? `${(data as MergeSpotRequest).sourcespotname} → ${(data as MergeSpotRequest).targetspotname}`
                  : request.type === "rename"
                  ? `${(data as SpotAddRequest).oldname} → ${(data as SpotAddRequest).newname}`
                  : (data as SpotAddRequest).spotname;
                const typeLabel = request.type === "merge" ? "Merge" : request.type === "rename" ? "Rename" : "Add";
                const typeColor = request.type === "merge" ? "purple" : request.type === "rename" ? "blue" : "green";

                return (
                  <button
                    key={`req-${request.type}-${request.id}`}
                    onClick={() => handleRequestClick(request)}
                    className="w-full px-3 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                  >
                    <div className={`w-8 h-8 bg-${typeColor}-100 rounded-full flex items-center justify-center flex-shrink-0`}
                      style={{ backgroundColor: typeColor === "purple" ? "#f3e8ff" : typeColor === "blue" ? "#dbeafe" : "#dcfce7" }}>
                      {request.type === "merge" ? (
                        <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      ) : request.type === "rename" ? (
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{name}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded`}
                          style={{
                            backgroundColor: typeColor === "purple" ? "#f3e8ff" : typeColor === "blue" ? "#dbeafe" : "#dcfce7",
                            color: typeColor === "purple" ? "#7c3aed" : typeColor === "blue" ? "#2563eb" : "#16a34a"
                          }}>
                          {typeLabel}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-blue-600 font-medium">View</span>
                  </button>
                );
              })}
              {filteredRequests.length > INITIAL_LIMIT && (
                <button
                  onClick={() => toggleSection("requests")}
                  className="w-full px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors text-center"
                >
                  {expandedSections.requests ? "Show less" : `Show ${filteredRequests.length - INITIAL_LIMIT} more`}
                </button>
              )}
            </div>
          )}

          {/* Live Riders Section */}
          {filteredUsers.length > 0 && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live Riders ({filteredUsers.length})
              </div>
              {displayedUsers.map((session) => (
                <button
                  key={`user-${session.id}`}
                  onClick={() => handleUserClick(session.id)}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                >
                  <div className="relative flex-shrink-0">
                    <img
                      src={
                        session.user.profilepicid
                          ? `${ENDPOINTS.IMAGES.PROFILE}/${session.user.profilepicid}`
                          : DEFAULT_PROFILE_PIC
                      }
                      alt={session.user.name}
                      className="w-8 h-8 rounded-full object-cover border-2 border-green-500"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DEFAULT_PROFILE_PIC;
                      }}
                    />
                    {session.isstreaming && (
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {session.user.name}
                      </span>
                      {session.user.ispro && (
                        <span className="text-[10px] font-bold bg-yellow-400 text-black px-1 rounded">
                          PRO
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{session.maxheight.toFixed(1)}m max</span>
                      {session.spot?.name && (
                        <>
                          <span>·</span>
                          <span className="truncate">{session.spot.name}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">Jump</span>
                </button>
              ))}
              {filteredUsers.length > INITIAL_LIMIT && (
                <button
                  onClick={() => toggleSection("riders")}
                  className="w-full px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors text-center"
                >
                  {expandedSections.riders ? "Show less" : `Show ${filteredUsers.length - INITIAL_LIMIT} more`}
                </button>
              )}
            </div>
          )}

          {/* Spots Section */}
          {allSpots.length > 0 && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <svg
                  className="w-3.5 h-3.5 text-red-500"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
                Spots ({allSpots.length})
              </div>
              {displayedSpots.map((spot) => (
                <button
                  key={`spot-${spot.id}`}
                  onClick={() => handleSpotClick(spot)}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                >
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-red-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {spot.name}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        #{spot.id}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {spot.alpha2 && (
                        <img
                          src={`https://flagcdn.com/w20/${spot.alpha2.toLowerCase()}.png`}
                          alt={spot.alpha2}
                          className="w-3.5 h-auto"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      )}
                      <span className="text-xs text-gray-500">
                        {spot.country || spot.alpha2}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">Jump</span>
                </button>
              ))}
              {allSpots.length > INITIAL_LIMIT && (
                <button
                  onClick={() => toggleSection("spots")}
                  className="w-full px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors text-center"
                >
                  {expandedSections.spots ? "Show less" : `Show ${allSpots.length - INITIAL_LIMIT} more`}
                </button>
              )}
            </div>
          )}

          {/* Locations Section (Nominatim) */}
          {locations.length > 0 && (
            <div>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <svg
                  className="w-3.5 h-3.5 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Locations ({locations.length})
              </div>
              {displayedLocations.map((location) => (
                <button
                  key={`loc-${location.place_id}`}
                  onClick={() => handleLocationClick(location)}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {location.display_name.split(",")[0]}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {shortenDisplayName(location.display_name)}
                    </div>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">Jump</span>
                </button>
              ))}
              {locations.length > INITIAL_LIMIT && (
                <button
                  onClick={() => toggleSection("locations")}
                  className="w-full px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors text-center"
                >
                  {expandedSections.locations ? "Show less" : `Show ${locations.length - INITIAL_LIMIT} more`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapSearchBar;
